"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "Subscription gate",
    rule: "Creating a Division requires the Division feature to be active in your plan",
  },
  {
    topic: "Unique name",
    rule: "Division names must be unique per company, checked case-insensitively",
  },
  {
    topic: "Hierarchy order",
    rule: "A Division must exist before you can create a Branch under it, and a Branch before a Warehouse under that",
  },
  {
    topic: "Division Code",
    rule: "Pre-filled with the next DIV- number and locked in the form — you can't type your own",
  },
  {
    topic: "Deactivating",
    rule: "Blocked while the division has any open Quote, Sales Order, Delivery Challan, Invoice, Purchase Order, GRPO, Bill, Recurring Invoice, Recurring Expense, Recurring Bill, Production Order, Inventory Adjustment, Debit Note, or Credit Note",
  },
  {
    topic: "Deleting",
    rule: "Far stricter than deactivating — blocked if the division was ever referenced by any document at all, even old and fully closed ones, and blocked outright if it still has any Branch or Warehouse under it",
  },
  {
    topic: "Manager field",
    rule: "The list has a Manager column and you can search by manager, but there's no field on the Add or Edit form to actually set one",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Division sits at the top of a required hierarchy" },
      { heading: "2. Creating one depends on your subscription plan" },
      { heading: "3. Deactivating checks for open documents, not history" },
      { heading: "4. Deleting is far stricter than deactivating" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function DivisionPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Division
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone setting up the top level of the company&apos;s Division, Branch, and
            Warehouse structure
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <span className="geist-medium text-[#111827]">Division</span> is
            the widest unit your company splits into. Every{" "}
            <span className="geist-medium text-[#111827]">Branch</span> belongs
            to one Division, every{" "}
            <span className="geist-medium text-[#111827]">Warehouse</span>{" "}
            belongs to one Branch, and that order isn&apos;t optional. You
            build the structure top-down.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Hierarchy */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Division sits at the top of a required hierarchy
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            You can&apos;t skip a level. The order the app enforces is fixed.
          </p>
          <Definitions
            items={[
              {
                term: "Division",
                desc: "The top level. Created first, with nothing above it.",
              },
              {
                term: "Branch",
                desc: "Created under a Division. You need a Division to exist before Branch creation lets you pick one.",
              },
              {
                term: "Warehouse",
                desc: "Created under a Branch, the same way. Stock and transactions ultimately trace back up to a Division through this chain. Settings → Organization defaults picks a default Division / Branch / Warehouse from this same hierarchy for new sales documents.",
              },
              {
                term: "Division Name",
                desc: "Must be unique across your company. The check ignores letter case, so \"Freedom\" and \"FREEDOM\" count as the same name.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Division list showing Code, Name, Address, and Status columns." />

          {/* 2. Subscription */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Creating one depends on your subscription plan
          </h3>
          <Definitions
            items={[
              {
                term: "Division feature",
                desc: "Has to be active on your plan before the Add Division screen will let you save one at all.",
              },
              {
                term: "Division Code",
                desc: "Shown pre-filled with the next available DIV- number the moment you open the form, and the field is locked. You're not choosing or typing it.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Division form with the Division Code field disabled and pre-filled." />

          {/* 3. Deactivate */}
          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Deactivating checks for open documents, not history
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <span className="geist-medium text-[#111827]">Mark as Inactive</span>{" "}
            only looks at what&apos;s currently unfinished under this division.
          </p>
          <Definitions
            items={[
              {
                term: "Blocked while open",
                desc: "Any Quote, Sales Order, Delivery Challan, Invoice, Purchase Order, GRPO, Bill, active Recurring Invoice, Recurring Expense, or Recurring Bill, in-progress Production Order, draft Inventory Adjustment, or unclosed Debit Note or Credit Note stops the deactivation.",
              },
              {
                term: "Closed history doesn't count",
                desc: "A division with nothing but paid, completed, or cancelled documents against it can be deactivated freely.",
              },
              {
                term: "Reactivating",
                desc: "Has no equivalent check. You can turn a division back on any time.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Mark as Inactive action in a division row's Actions menu." />

          {/* 4. Delete */}
          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Deleting is far stricter than deactivating
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <span className="geist-medium text-[#111827]">Delete</span> asks a
            completely different question than{" "}
            <span className="geist-medium text-[#111827]">deactivate</span>{" "}
            does: not whether anything&apos;s still open, but whether the
            division was ever used at all.
          </p>
          <Definitions
            items={[
              {
                term: "Children first",
                desc: "Blocked outright if any Branch or Warehouse still exists under this division, regardless of their own status.",
              },
              {
                term: "Any historical reference",
                desc: "Also blocked if the division appears on an Invoice, Bill, Delivery Challan, GRPO, Purchase Order, Credit Note, Debit Note, Recurring Invoice, Recurring Bill, Sales Order, Quote, Sales Target, or Payment Received, even one that's long since closed.",
              },
            ]}
          />
          <Callout type="warning">
            A division that&apos;s actually been used for real business, even
            once, effectively can&apos;t be deleted. Deactivate is the
            realistic option once history exists.
          </Callout>
          <Screenshot caption="Screenshot: the delete confirmation blocked by an in-use division." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
          <Link
            href="/documentation/books/all-master/branch"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Branch <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Created under a Division, one level down
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/warehouse-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Warehouse Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Created under a Branch, the bottom of the same hierarchy
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
